import { useMemo } from "react";
import { useQueries, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type {
  CalendarMonthPlanListRow,
  CalendarMonthPlanWithSlots,
  CalendarPlanSlot,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { planQueryOptions } from "./calendarCache";
import { briefBySlot, planBriefsKey } from "./useProductionBrief";

type BriefsData = Parameters<typeof briefBySlot>[0];

// Module scope, so the combined results stay referentially stable between renders.
function combinePlans(results: UseQueryResult<CalendarMonthPlanWithSlots>[]) {
  return {
    slots: results.flatMap((result) => result.data?.slots ?? []),
    loading: results.some((result) => result.isLoading),
  };
}

function combineBriefs(results: UseQueryResult<BriefsData>[]) {
  const map = new Map<string, BriefRailInfo>();
  for (const result of results) for (const [id, info] of briefBySlot(result.data)) map.set(id, info);
  return map;
}

function totalOf(plan: CalendarMonthPlanListRow): number {
  return Object.values(plan.slotCounts ?? {}).reduce((sum, n) => sum + (n ?? 0), 0);
}

/**
 * Other months' posts and briefs, read with the same keys and endpoints as the
 * month itself so the cache is shared: the List view's Needs you queue runs
 * across months, and the week shows the neighbouring month's days. Reads only;
 * the open month keeps its own poll.
 */
export function useOtherMonths(
  plans: readonly CalendarMonthPlanListRow[] | undefined,
  options: {
    enabled: boolean;
    excludePlanId?: string | null;
    /** Only these months, as "2026-12". All months with posts when absent. */
    only?: readonly string[];
    withBriefs?: boolean;
  },
): { slots: CalendarPlanSlot[]; briefBySlot: Map<string, BriefRailInfo>; loading: boolean } {
  const qc = useQueryClient();
  const onlyKey = options.only?.join(",") ?? null;
  const targets = useMemo(() => {
    const only = onlyKey ? new Set(onlyKey.split(",")) : null;
    return (plans ?? []).filter((plan) => {
      if (plan.id === options.excludePlanId) return false;
      if (only && !only.has(`${plan.year}-${String(plan.month).padStart(2, "0")}`)) return false;
      return totalOf(plan) > 0;
    });
  }, [plans, options.excludePlanId, onlyKey]);

  const plansRead = useQueries({
    queries: targets.map((plan) => ({
      ...planQueryOptions(qc, plan.id),
      enabled: options.enabled,
      staleTime: 60_000,
    })),
    combine: combinePlans,
  });

  const briefs = useQueries({
    queries: targets.map((plan) => ({
      queryKey: planBriefsKey(plan.id),
      queryFn: async () => {
        const res = await axiosInstance.get<BriefsData>(`/content-calendar/plans/${plan.id}/briefs`);
        return res.data;
      },
      enabled: options.enabled && !!options.withBriefs && (plan.slotCounts?.CLIENT_APPROVED ?? 0) > 0,
      staleTime: 60_000,
    })),
    combine: combineBriefs,
  });

  return { slots: plansRead.slots, briefBySlot: briefs, loading: plansRead.loading };
}
